import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';
import {
  Breadcrumbs,
  BreadcrumbsItem,
  Card,
  CardContent,
  CardDetail,
  CardExpiration,
  CardIcon,
  CardIconStatus,
  CardLabel,
  CardValue,
  getExpirationStatus,
  IgnoredChecksBreakdown,
  Label,
  LoaderSpin,
  PageActions,
  PageHeader,
  PageHeaderContent,
  PageTitle,
  Section,
  SectionHeader,
  SplitChecksToggleBreakdown,
  StatusPayment,
  ToggleableBreakdown,
  TooltipIcon,
  TooltipWrapper,
} from 'components';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { routeConstants } from 'routes';
import { PaymentMethodAccountNumber } from 'screens/Settings/components';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getUserPayoutCalculationsRequest, getUserPayoutRequest, Payout } from 'store/Payout';
import { IStore } from 'store/types';
import { IPayout, IUserPayoutCalculations, PayoutRefType, PayoutStatus } from 'types';
import { buildEmployeeOverrideTicketBreakdown, getPaymentTypeIcon } from 'utils';
import { sortAlphaNumerically } from 'utils/arrays';
import { centsToDollarsDisplay } from 'utils/currency';
import { StyledCardRow } from '../components';

const StyledPageTitle = styled(PageTitle)`
  text-align: center;

  &.no-wrap h1 {
    white-space: nowrap;
  }
`;

const StyledStatusPayment = styled(StatusPayment)<{ desktop?: boolean }>`
  display: ${({ desktop }) => (desktop ? 'none' : 'flex')};

  div:first-child {
    color: ${theme.color.neutral[300]};
  }

  span {
    white-space: nowrap;
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    display: ${({ desktop }) => (desktop ? 'flex' : 'none')};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  margin-top: ${toRem(40)};
`;

const EmployeePayoutReport = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  margin-top: ${toRem(40)};
  background: ${theme.color.neutral[50]};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${theme.borderRadius.small}px;
  padding: ${toRem(16)};

  > * {
    margin-bottom: ${toRem(16)};

    &:last-child {
      margin: 0;
    }
  }
`;

const EmployeePayoutDetails = () => {
  const dispatch = useDispatch();
  const params = useParams<{ payoutId: string }>();
  const { pathname } = useLocation();

  // User
  const [payout, setPayout] = useState<IPayout>();
  const [payoutCalculations, setPayoutCalculations] = useState<IUserPayoutCalculations>();
  const isPayoutLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payout.GET_USER_PAYOUT_REQUEST));
  const isCalculationLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payout.GET_USER_PAYOUT_CALCULATIONS_REQUEST));

  // Payout Details
  const isPayroll = payout?.status === PayoutStatus.PAYROLL;
  const paymentDestination = payout && payout.paymentDestination;
  const isCardExpired = paymentDestination?.expDate ? getExpirationStatus(paymentDestination?.expDate) === 'Expired' : false;
  const businessDate = dayjs(payout?.paymentDate).format('MM-DD-YYYY');
  const payoutSubmittedDate = dayjs(payout?.createdAt).local().format('MM-DD-YYYY');
  const payoutSubmittedTime = dayjs(payout?.createdAt).local().format('HH:mm A z');

  // Component State
  const isCalculatorPayout = payout?.refType && payout?.refType === PayoutRefType.CALCULATOR;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (params?.payoutId) dispatch(getUserPayoutRequest(params?.payoutId, setPayout));
  }, []);

  useEffect(() => {
    if (isCalculatorPayout && payout && payoutCalculations?.payoutId !== payout.id) dispatch(getUserPayoutCalculationsRequest(payout?.id, setPayoutCalculations));
  }, [payout]);

  const calcResults = payoutCalculations?.results;

  const renderSegmentBreakdown = useMemo(
    () =>
      calcResults?.map((segment) => {
        const calculations = segment.employees.filter((c) => c.received.length || c.contributions.length || c.retained?.total > 0);

        if (!calculations?.length) return <></>;

        return calculations
          .filter((r) => r.calculated.total > 0 || r.totals.totalMinWorked > 0)
          .map((calculation, idx) => (
            <ToggleableBreakdown
              key={`segment-breakdown-${idx}`}
              categoryGroups={segment.meta.categoryGroups}
              calculatorRules={segment.rules}
              calculation={calculation}
              segmentName={segment.segment}
              creditCardFee={segment.creditCardPercentFee}
            />
          ));
      }),
    [calcResults]
  );

  const renderOverrideBreakdown = useMemo(() => {
    if (!calcResults) return null;

    const { splitChecks, ignoredChecks } = buildEmployeeOverrideTicketBreakdown(calcResults.flatMap((result) => result.employees));

    return (
      <>
        {(!!splitChecks.received?.length || !!splitChecks.contributions?.length) && <SplitChecksToggleBreakdown splitChecks={splitChecks} />}
        {!!ignoredChecks.length && <IgnoredChecksBreakdown ignoredCheckData={ignoredChecks.sort(sortAlphaNumerically('displayId'))} />}
      </>
    );
  }, [calcResults]);

  return (
    <>
      <PageHeader data-cy="page-header" hasStats>
        <Breadcrumbs data-cy="breadcrumbs">
          <BreadcrumbsItem label="Dashboard" to={routeConstants.private.rootPath} />
          <BreadcrumbsItem label="Payment ID" />
        </Breadcrumbs>
        <PageHeaderContent stackable>
          <PageActions mobileMarginTop display="row">
            <StyledPageTitle data-cy="payout-id-header" title={params?.payoutId} isLoading={isPayoutLoading} />
            <StyledStatusPayment desktop data-cy="payout-status-header" status={payout?.status} isLoading={isPayoutLoading} subStatusLabel={payout?.alert?.label} subStatusTooltip={payout?.alert?.message} />
          </PageActions>
          <StyledStatusPayment data-cy="payout-status-header" status={payout?.status} isLoading={isPayoutLoading} subStatusLabel={payout?.alert?.label} subStatusTooltip={payout?.alert?.message} />
          <PageActions display="column" align="end">
            <TooltipWrapper>
              <Label>Net Total</Label>
              <TooltipIcon id="payout-net-tooltip" positionStrategy="fixed">
                Net Total after deducting applicable fees
              </TooltipIcon>
            </TooltipWrapper>
            <StyledPageTitle data-cy="payout-net-header" className="no-wrap" title={centsToDollarsDisplay(payout?.net)} isLoading={isPayoutLoading} />
          </PageActions>
        </PageHeaderContent>

        <StyledCardRow data-cy="info-card-row">
          <Card>
            <CardIcon icon="Site" />
            <CardContent data-cy="site-card">
              <CardLabel isLoading={isPayoutLoading}>Site</CardLabel>
              <CardValue isLoading={isPayoutLoading}>{payout?.site?.name}</CardValue>
            </CardContent>
          </Card>
          <Card>
            <CardIcon icon="Calendar" />
            <CardContent data-cy="submitted-date-card">
              <CardLabel isLoading={isPayoutLoading}>Submitted</CardLabel>
              <CardValue isLoading={isPayoutLoading}>{payoutSubmittedDate}</CardValue>
              <CardDetail isLoading={isPayoutLoading}>{payoutSubmittedTime}</CardDetail>
            </CardContent>
          </Card>
          <Card>
            <CardIcon icon="Calendar" />
            <CardContent data-cy="business-date-card">
              <CardLabel isLoading={isPayoutLoading}>Business Date</CardLabel>
              <CardValue isLoading={isPayoutLoading}>{businessDate}</CardValue>
            </CardContent>
          </Card>
          <Card error={isCardExpired} data-cy={isCardExpired ? 'expired-card-wrapper' : undefined}>
            {paymentDestination ? (
              <CardIcon bgColor={theme.color.neutral[100]} icon={getPaymentTypeIcon(paymentDestination?.network)} isLoading={isPayoutLoading} />
            ) : isPayroll ? (
              <CardIcon bgColor={theme.color.neutral[100]} icon="Receipt" isLoading={isPayoutLoading} />
            ) : (
              <CardIconStatus color={Appearance.NEUTRAL} icon="Payment" isLoading={isPayoutLoading} />
            )}
            <CardContent data-cy="payout-method-card">
              <CardLabel isLoading={isPayoutLoading}>Payment Method</CardLabel>
              {isPayroll ? (
                <CardValue isLoading={isPayoutLoading}>Payroll</CardValue>
              ) : (
                <CardValue isLoading={isPayoutLoading}>{paymentDestination ? <PaymentMethodAccountNumber showIcon={false} cardType={paymentDestination?.network} cardNumber={paymentDestination.last4} /> : 'No card on file'}</CardValue>
              )}
              {paymentDestination?.expDate && <CardExpiration date={paymentDestination?.expDate} isLoading={isPayoutLoading} />}
            </CardContent>
          </Card>
        </StyledCardRow>
      </PageHeader>

      <Section afterHeaderCardRow>
        <SectionHeader title="Note" isLoading={isPayoutLoading} />
        {!isPayoutLoading && payout?.notes ? payout.notes : '--'}

        {isCalculationLoading && (
          <LoadingContainer>
            <LoaderSpin size={40} />
          </LoadingContainer>
        )}

        {isCalculatorPayout && calcResults && payout && (
          <EmployeePayoutReport data-cy="payout-calculation-breakdown-report">
            {renderSegmentBreakdown}
            {renderOverrideBreakdown}
          </EmployeePayoutReport>
        )}
      </Section>
    </>
  );
};

export default EmployeePayoutDetails;
